import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Toaster, toast } from 'sonner';
import Footer from '../components/Footer';
import {
  ArrowLeft,
  Loader2,
  Mail,
  Phone,
  Globe,
  MapPin,
  Building2,
  GripVertical,
  ChevronLeft,
  ChevronRight,
  BarChart3,
  Trophy,
  Users,
  Target,
  XCircle,
  TrendingUp,
  Linkedin,
  ExternalLink,
  Inbox,
  Bell,
  ShieldCheck
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGE_CONFIG = {
  form_lead:   { label: 'Lead da Form',    color: 'bg-fuchsia-50',   accent: 'border-fuchsia-400',  textColor: 'text-fuchsia-700',  icon: Inbox,       badge: 'bg-fuchsia-100 text-fuchsia-700' },
  new:         { label: 'Da Contattare',  color: 'bg-slate-100',    accent: 'border-slate-400',    textColor: 'text-slate-700',    icon: Users,       badge: 'bg-slate-200 text-slate-700' },
  contacted:   { label: 'Contattato',     color: 'bg-blue-50',      accent: 'border-blue-400',     textColor: 'text-blue-700',     icon: Mail,        badge: 'bg-blue-100 text-blue-700' },
  interested:  { label: 'Interessato',    color: 'bg-amber-50',     accent: 'border-amber-400',    textColor: 'text-amber-700',    icon: Target,      badge: 'bg-amber-100 text-amber-700' },
  negotiation: { label: 'In Trattativa',  color: 'bg-purple-50',    accent: 'border-purple-400',   textColor: 'text-purple-700',   icon: TrendingUp,  badge: 'bg-purple-100 text-purple-700' },
  won:         { label: 'Cliente',        color: 'bg-emerald-50',   accent: 'border-emerald-400',  textColor: 'text-emerald-700',  icon: Trophy,      badge: 'bg-emerald-100 text-emerald-700' },
  lost:        { label: 'Perso',          color: 'bg-red-50',       accent: 'border-red-300',      textColor: 'text-red-600',      icon: XCircle,     badge: 'bg-red-100 text-red-600' },
};

const STAGES = ['form_lead', 'new', 'contacted', 'interested', 'negotiation', 'won', 'lost'];

function LeadCard({ lead, onMove, stages }) {
  const [moving, setMoving] = useState(false);
  const currentIdx = stages.indexOf(lead.pipeline_stage || 'new');

  const moveForward = async () => {
    if (currentIdx < stages.length - 1) {
      setMoving(true);
      await onMove(lead.id, stages[currentIdx + 1]);
      setMoving(false);
    }
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className={`bg-white rounded-lg border p-3 shadow-sm hover:shadow-md transition-shadow group relative ${lead.unread ? 'border-fuchsia-300 ring-2 ring-fuchsia-100' : 'border-slate-200'}`}
      data-testid={`pipeline-card-${lead.id}`}
    >
      {lead.unread && (
        <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-fuchsia-500 rounded-full ring-2 ring-white" title="Nuovo lead da form" data-testid={`unread-dot-${lead.id}`} />
      )}
      <div className="flex items-start justify-between mb-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <h4 className="font-semibold text-slate-900 text-sm truncate">{lead.name}</h4>
            {lead.vies_verified && (
              <span
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 text-[9px] font-bold uppercase tracking-wide"
                title={`Verificata su VIES\n${lead.vies_company_name || ''}\n${lead.vies_address || ''}`}
                data-testid={`vies-badge-${lead.id}`}
              >
                <ShieldCheck className="w-2.5 h-2.5" /> VIES
              </span>
            )}
          </div>
          {(lead.vies_company_name || lead.company) && (
            <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
              <Building2 className="w-3 h-3 flex-shrink-0" />
              <span className="truncate">{lead.vies_company_name || lead.company}</span>
            </p>
          )}
          {lead.vat && (
            <p className="text-[10px] font-mono text-slate-400 mt-0.5">P.IVA {lead.vat}</p>
          )}
          {lead.message && (
            <p className="text-xs text-slate-600 italic mt-1 line-clamp-2">"{lead.message}"</p>
          )}
        </div>
        {moving ? (
          <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
        ) : (
          currentIdx < stages.length - 2 && (
            <button
              onClick={moveForward}
              className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-slate-100"
              title={`Sposta a: ${STAGE_CONFIG[stages[currentIdx + 1]]?.label}`}
              data-testid={`move-forward-${lead.id}`}
            >
              <ChevronRight className="w-4 h-4 text-slate-500" />
            </button>
          )
        )}
      </div>

      <div className="space-y-1">
        {lead.email && (
          <p className="text-xs text-slate-600 flex items-center gap-1 truncate">
            <Mail className="w-3 h-3 flex-shrink-0" /> {lead.email}
          </p>
        )}
        {lead.phone && (
          <p className="text-xs text-slate-600 flex items-center gap-1">
            <Phone className="w-3 h-3 flex-shrink-0" /> {lead.phone}
          </p>
        )}
        {lead.location && (
          <p className="text-xs text-slate-500 flex items-center gap-1">
            <MapPin className="w-3 h-3 flex-shrink-0" /> {lead.location}
          </p>
        )}
      </div>

      {/* Quick move buttons */}
      <div className="flex gap-1 mt-2 pt-2 border-t border-slate-100 flex-wrap">
        {stages.filter(s => s !== (lead.pipeline_stage || 'new')).map(stage => {
          const cfg = STAGE_CONFIG[stage];
          return (
            <button
              key={stage}
              onClick={() => onMove(lead.id, stage)}
              className={`text-[10px] px-1.5 py-0.5 rounded ${cfg.badge} hover:opacity-80 transition`}
              data-testid={`move-to-${stage}-${lead.id}`}
            >
              {cfg.label}
            </button>
          );
        })}
      </div>
    </motion.div>
  );
}

export default function PipelinePage() {
  const navigate = useNavigate();
  const [pipeline, setPipeline] = useState({});
  const [counts, setCounts] = useState({});
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [draggedLead, setDraggedLead] = useState(null);
  const scrollRef = useRef(null);

  const scrollBy = (delta) => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: delta, behavior: 'smooth' });
    }
  };

  const fetchPipeline = useCallback(async () => {
    try {
      const [pipeRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/pipeline`),
        axios.get(`${API_URL}/pipeline/stats`)
      ]);
      setPipeline(pipeRes.data.pipeline);
      setCounts(pipeRes.data.counts);
      setStats(statsRes.data);
    } catch (err) {
      toast.error('Errore caricamento pipeline');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPipeline(); }, [fetchPipeline]);

  const moveLead = async (leadId, newStage) => {
    try {
      await axios.put(`${API_URL}/pipeline/${leadId}/move`, { pipeline_stage: newStage });
      await fetchPipeline();
      toast.success(`Lead spostato in "${STAGE_CONFIG[newStage].label}"`);
    } catch {
      toast.error('Errore spostamento lead');
    }
  };

  const markAllRead = async () => {
    try {
      await axios.post(`${API_URL}/leads/mark-all-form-read`);
      await fetchPipeline();
      toast.success('Tutti i lead form segnati come letti');
    } catch {
      toast.error('Errore');
    }
  };

  const handleDragStart = (e, lead) => {
    setDraggedLead(lead);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetStage) => {
    e.preventDefault();
    if (draggedLead && draggedLead.pipeline_stage !== targetStage) {
      moveLead(draggedLead.id, targetStage);
    }
    setDraggedLead(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  const totalLeads = Object.values(counts).reduce((a, b) => a + b, 0);

  return (
    <div className="min-h-screen bg-slate-50">
      <Toaster position="top-right" richColors />

      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 gap-2">
            <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard" className="flex-shrink-0">
              <ArrowLeft className="w-4 h-4 sm:mr-2" /><span className="hidden sm:inline">Dashboard</span>
            </Button>
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              <div className="hidden sm:flex w-10 h-10 rounded-xl bg-indigo-600 text-white items-center justify-center flex-shrink-0">
                <BarChart3 className="w-5 h-5" />
              </div>
              <span className="font-bold text-base sm:text-xl text-slate-900 tracking-tight truncate">CRM Pipeline</span>
              {(counts.form_lead > 0) && (
                <button onClick={markAllRead}
                  className="inline-flex items-center gap-1 px-2 sm:px-2.5 py-1 rounded-full bg-fuchsia-100 text-fuchsia-700 text-xs font-semibold hover:bg-fuchsia-200 transition flex-shrink-0"
                  data-testid="mark-all-form-read-btn"
                  title="Segna tutti i lead form come letti"
                >
                  <Bell className="w-3 h-3" /> {counts.form_lead} <span className="hidden sm:inline">nuovi</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">

        {/* Stats Summary */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="pipeline-stats">
            <Card className="border-slate-200">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                  <Users className="w-5 h-5 text-slate-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{totalLeads}</p>
                  <p className="text-xs text-slate-500">Totale Lead</p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-slate-200">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Target className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.active}</p>
                  <p className="text-xs text-slate-500">Attivi</p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-slate-200">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <Trophy className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.stages?.won || 0}</p>
                  <p className="text-xs text-slate-500">Clienti</p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-slate-200">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.conversion_rate}%</p>
                  <p className="text-xs text-slate-500">Conversione</p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Kanban Board */}
        {totalLeads === 0 ? (
          <Card className="border-slate-200">
            <CardContent className="p-12 text-center">
              <BarChart3 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-700 mb-2">Pipeline vuota</h3>
              <p className="text-slate-500 mb-4">Salva dei lead dalla Ricerca Batch o dal Google Maps Scraper per iniziare</p>
              <Button onClick={() => navigate('/batch-search')} className="bg-amber-600 hover:bg-amber-700 text-white">
                Vai alla Ricerca Batch
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="relative">
            <button
              onClick={() => scrollBy(-320)}
              className="hidden lg:flex absolute -left-4 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-white border border-slate-200 shadow-md items-center justify-center hover:bg-slate-50 transition"
              aria-label="Scorri a sinistra"
              data-testid="kanban-scroll-left"
            >
              <ChevronLeft className="w-4 h-4 text-slate-600" />
            </button>
            <button
              onClick={() => scrollBy(320)}
              className="hidden lg:flex absolute -right-4 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-white border border-slate-200 shadow-md items-center justify-center hover:bg-slate-50 transition"
              aria-label="Scorri a destra"
              data-testid="kanban-scroll-right"
            >
              <ChevronRight className="w-4 h-4 text-slate-600" />
            </button>
            <div ref={scrollRef} className="kanban-scroll flex gap-3 sm:gap-4 overflow-x-auto pb-4 snap-x snap-mandatory" data-testid="kanban-board">
            {STAGES.map(stage => {
              const cfg = STAGE_CONFIG[stage];
              const leads = pipeline[stage] || [];
              const Icon = cfg.icon;

              return (
                <div
                  key={stage}
                  className={`flex-shrink-0 w-[85vw] sm:w-[300px] lg:w-[260px] snap-start rounded-xl ${cfg.color} border-t-4 ${cfg.accent}`}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, stage)}
                  data-testid={`pipeline-column-${stage}`}
                >
                  {/* Column header */}
                  <div className="p-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Icon className={`w-4 h-4 ${cfg.textColor}`} />
                      <span className={`font-semibold text-sm ${cfg.textColor}`}>{cfg.label}</span>
                    </div>
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${cfg.badge}`}>
                      {leads.length}
                    </span>
                  </div>

                  {/* Cards */}
                  <div className="p-2 space-y-2 max-h-[calc(100vh-320px)] overflow-y-auto">
                    <AnimatePresence>
                      {leads.map(lead => (
                        <div
                          key={lead.id}
                          draggable
                          onDragStart={(e) => handleDragStart(e, lead)}
                          className="cursor-grab active:cursor-grabbing"
                        >
                          <LeadCard lead={lead} onMove={moveLead} stages={STAGES} />
                        </div>
                      ))}
                    </AnimatePresence>
                    {leads.length === 0 && (
                      <div className="text-center py-8">
                        <p className="text-xs text-slate-400">Trascina qui un lead</p>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
